import { useEffect, useRef, useState, useCallback } from "react";
import { LiveMediaHelper } from "@kaltura/mediaspace-shared-utils";
import Lobby from "../lobby/Lobby";
import { CommonHeroLayout } from "../layout/CommonHeroLayout";
import { HeroProps } from "../HeroProps";
import { LiveMediaPlayerV3, LiveMediaPlayerV3Props } from "@kaltura/mediaspace-shared-player";
import { getPropsToPass, useThumbnailSizing } from "../helpers";
import { MediaLobbyDataContainer } from "../lobby/lobby-data/LobbyData";
import { useCncReactions } from "@mediaspace/features/cnc";

export interface TvHeroProps extends HeroProps, LiveMediaPlayerV3Props {
    playerConfig: any;
}

declare global {
    interface Window {
        tvEntryPagePlayer: () => void;
    }
}

/**
 * wrapper component for TV media - lobby and player
 */
const TvHero = (props: TvHeroProps) => {
    const { playerConfig, playlist, actions = [], thumbnailUrl, entry, schedulingData } = props;

    const STREAM_POLL_INTERVAL = 10000;
    const [player, setPlayer] = useState<any>(null);
    const [showLobby, setShowLobby] = useState(true);
    const pollingInterval = useRef(0);

    const { startReactions, endReactions } = useCncReactions();

    // tslint:disable-next-line:no-any
    const setupPlayerListeners = (playerInstance: any) => {
        window["tvEntryPagePlayer"] = playerInstance;
        playerInstance.addEventListener(playerInstance.Event.Core.PLAYER_DESTROY, teardownPlayerListeners);
        playerInstance.addEventListener(playerInstance.Event.Core.PLAYBACK_ENDED, handlePlaybackEnd);
        if (playlist) {
            playerInstance.plugins.kava.config.playlistId = playlist.id;
        }
        setPlayer(playerInstance);
    };

    // tslint:disable-next-line:no-any
    const teardownPlayerListeners = (event: any) => {
        // remove listeners
        if (!player) {
            return;
        }
        player.removeEventListener(player.Event.Core.PLAYER_DESTROY, teardownPlayerListeners);
        player.removeEventListener(player.Event.Core.PLAYBACK_ENDED, handlePlaybackEnd);
    };

    // tslint:disable-next-line:no-any
    const handlePlaybackEnd = (event: any) => {
        setShowLobby(true);
        // start polling again
        startPolling();
        endReactions();
    };

    const pollStream = useCallback(() => {
        console.debug(">> polling primary stream... ");
        return LiveMediaHelper
            .pollStream(playerConfig.sources.hls[0].url)
            .then((value) => {
                console.debug(">> stream is alive");
                // promise is always resolved with true
                handleStreamStart();
                return Promise.resolve(true);
            })
            .catch((err) => {
                console.debug(">> stream is dead");
                console.error(err);
                // else (primary is not live)
                if (playerConfig.plugins?.liveFallback?.fallbackUrl) {
                    // got secondary url, poll it
                    console.debug(">> polling secondary stream... ");
                    return LiveMediaHelper
                        .pollStream(playerConfig.plugins.liveFallback.fallbackUrl)
                        .then((value) => {
                            console.debug(">> stream is alive");
                            // promise is always resolved with true
                            handleStreamStart();
                            return Promise.resolve(true);
                        })
                        .catch((err2) => {
                            // else (not live) show hero section
                            console.debug(">> stream is dead");
                            console.error(err2);
                            setShowLobby(true);
                            return Promise.resolve(false);
                        });
                }
                // no secondary, media is not live.
                setShowLobby(true);
                return Promise.resolve(false);
            });
    }, [playerConfig?.plugins?.liveFallback?.fallbackUrl, playerConfig?.sources?.hls]);

    const handleStreamStart = () => {
        // if live, show player
        setShowLobby(false);

        startReactions();
    };

    const startPolling = useCallback(() => {
        // keep polling
        window.clearInterval(pollingInterval.current);
        pollingInterval.current = window.setInterval(
            () => {
                pollStream().then((isLive) => {
                    if (isLive) {
                        // no need to keep polling if stream is live
                        window.clearInterval(pollingInterval.current);
                    }
                });
            },
            STREAM_POLL_INTERVAL
        );
    }, [pollStream]);

    // logic of what to show when (player vs lobby), expected to only run once
    useEffect(() => {
        pollStream().then((result) => {
            if (!result) {
                startPolling();
            }
        });
        return () => {
            window.clearInterval(pollingInterval.current);
        };
    }, [pollStream, startPolling]);

    const { statusLabel, actionsToPass } = getPropsToPass(entry, schedulingData, actions);

    const sizedThumbnailUrl = useThumbnailSizing(thumbnailUrl);

    return (
        <CommonHeroLayout collapsedHero={props.collapse}>
            {showLobby && (
                <Lobby
                    thumbnailUrl={sizedThumbnailUrl}
                    statusLabel={statusLabel}
                    title={entry.name}
                    schedulingData={schedulingData}
                    actions={actionsToPass}
                    data={<MediaLobbyDataContainer {...props} />}
                />
            )}
            {!showLobby && (
                <LiveMediaPlayerV3
                    config={playerConfig}
                    onReady={setupPlayerListeners}
                    onError={() => void 0}
                    className="kms-ds-media-page-player"
                />
            )}
        </CommonHeroLayout>
    );
};

export default TvHero;
